<script setup lang="ts">
import { computed } from "vue";

import { FOCUS_STAGES } from "@/lib/focusCapacity";

import { STAGE_TREES, TREE_VIEW_BOX } from "./treePaths";

/**
 * The focus stage tree — Hazel / Rowan / Birch / Oak / Sequoia.
 *
 * Trees mean **focus attainment**. Rings mean knowledge. Never swap them
 * (PRD `docs/records/prd-platform-evolution.md` PE-7.3.6). The generic
 * sprout glyph (`components/ui/SproutGlyph.vue`) is the mark for the focus
 * *capability*; these five are rungs within it.
 *
 * Rendered inline rather than as a `<symbol>` sprite: the sprite's only real
 * win is de-duplication, and the tree appears once or twice per page, whereas
 * a sprite cannot express the compact variant without doubling to ten symbols
 * and adds a mount-order dependency that fails silently (a blank tree) if the
 * sprite is missing.
 */

const props = withDefaults(
  defineProps<{
    /** Rung index, 0–4. Out-of-range values clamp rather than render nothing. */
    stage: number;
    /**
     * Placement, per addendum §5:
     * - `inline` 22×24 — session-complete outcome line. Compact, never swayed.
     * - `card` 42×46 — Today's Focus card tile.
     * - `hero` 64×70 — "How focus grows" panel, and the stage-up celebration.
     */
    size?: "inline" | "card" | "hero";
    /** Slow ±3° sway. Ignored at `inline`, and killed by prefers-reduced-motion. */
    sway?: boolean;
    /** Force the compact skeleton. Defaults on at `inline`. */
    compact?: boolean;
  }>(),
  { size: "card", sway: false, compact: undefined },
);

const DIMENSIONS = {
  inline: { width: 22, height: 24 },
  card: { width: 42, height: 46 },
  hero: { width: 64, height: 70 },
} as const;

const stageIndex = computed(() =>
  Math.min(STAGE_TREES.length - 1, Math.max(0, Math.trunc(props.stage))),
);
const tree = computed(() => STAGE_TREES[stageIndex.value]);
const stageName = computed(() => FOCUS_STAGES[stageIndex.value].name);
const box = computed(() => DIMENSIONS[props.size]);

const isCompact = computed(() => props.compact ?? props.size === "inline");

/**
 * 1.2 reads as delicate linework at display sizes; below ~40px it disappears,
 * so the compact pass thickens to 1.5 and lets the canopy mass do the work.
 */
const strokeWidth = computed(() =>
  box.value.height >= 40 && !isCompact.value ? 1.2 : 1.5,
);

const strokes = computed(() =>
  isCompact.value
    ? tree.value.skeleton.filter((s) => s.tier !== "secondary")
    : tree.value.skeleton,
);

// Sway pivots near the base because the tree is rooted on the tile's bottom edge.
const swayClass = computed(() =>
  props.sway && props.size !== "inline" ? "rl-sway" : undefined,
);
</script>

<template>
  <svg
    :class="swayClass"
    :width="box.width"
    :height="box.height"
    :viewBox="TREE_VIEW_BOX"
    fill="none"
    role="img"
    :aria-label="stageName"
    :data-stage="stageName"
  >
    <g fill="var(--rl-green-500)">
      <circle
        v-for="(c, i) in tree.canopyCircles"
        :key="`c${i}`"
        :cx="c.cx"
        :cy="c.cy"
        :r="c.r"
      />
      <ellipse
        v-if="tree.canopyEllipse"
        :cx="tree.canopyEllipse.cx"
        :cy="tree.canopyEllipse.cy"
        :rx="tree.canopyEllipse.rx"
        :ry="tree.canopyEllipse.ry"
      />
      <path v-for="(d, i) in tree.canopyPaths" :key="`p${i}`" :d="d" />
    </g>

    <g
      stroke="var(--rl-green-800)"
      :stroke-width="strokeWidth"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path v-for="(s, i) in strokes" :key="`s${i}`" :d="s.d" />
    </g>

    <g v-if="tree.berries" fill="var(--rl-green-800)">
      <circle
        v-for="(b, i) in tree.berries"
        :key="`b${i}`"
        :cx="b.cx"
        :cy="b.cy"
        :r="b.r"
      />
    </g>
  </svg>
</template>
